import { useEffect, useState } from "react";

const CONFIG_URL = "/config.json";

/** Fallback when `/config.json` is missing or has no version (local/dev). */
export const FALLBACK_RELEASE_VERSION = "2.0.0";

type HubRuntimeConfig = {
  DXP_HUB_RELEASE_VERSION?: string;
  appConfig?: { DXP_HUB_RELEASE_VERSION?: string };
};

export function readReleaseVersion(data: unknown): string | undefined {
  if (data == null || typeof data !== "object") {
    return undefined;
  }

  const config = data as HubRuntimeConfig;
  const topLevel = config.DXP_HUB_RELEASE_VERSION?.trim();
  if (topLevel) {
    return topLevel;
  }

  const nested = config.appConfig?.DXP_HUB_RELEASE_VERSION?.trim();
  return nested || undefined;
}

let cached: string | null = null;
let inflight: Promise<string> | null = null;

export function resetReleaseVersionCacheForTests(): void {
  cached = null;
  inflight = null;
}

export async function loadReleaseVersion(): Promise<string> {
  if (cached) {
    return cached;
  }

  if (!inflight) {
    inflight = fetch(CONFIG_URL, { cache: "no-store" })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => readReleaseVersion(data) ?? FALLBACK_RELEASE_VERSION)
      .catch(() => FALLBACK_RELEASE_VERSION)
      .then((version) => {
        cached = version;
        return version;
      })
      .finally(() => {
        inflight = null;
      });
  }

  return inflight;
}

/**
 * Same-origin `fetch('/config.json')` → `DXP_HUB_RELEASE_VERSION` (not a build import).
 * @param enabled When false, skips fetch (e.g. off the home screen).
 */
export function useReleaseVersion(enabled = true): string {
  const [releaseVersion, setReleaseVersion] = useState(
    FALLBACK_RELEASE_VERSION,
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;

    void loadReleaseVersion().then((version) => {
      if (!cancelled) {
        setReleaseVersion(version);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return releaseVersion;
}
